
import { InspectionDef, LocalInspectionsData, PointValue, InspectionPointImage, getInspectionResultsData } from "../models/inspections/Inspections";
import { get, set } from "./Preferences";

const _inspectionsKey = "InspectionWorkOrders";

const getLocalInspections = async (): Promise<LocalInspectionsData[]> => {
    return (await get(_inspectionsKey)) as LocalInspectionsData[] || [];
};

const setLocalInspections = async (inspectionArray: LocalInspectionsData[]) => {
    return await set(_inspectionsKey, inspectionArray);
}

export const getInspectionFromLocal = async (
    inspectionId: string,
    workOrderId: string,
    equipmentId?: string
): Promise<LocalInspectionsData | null> => {
    const inspections = await getLocalInspections();
    const useEquipment = !workOrderId && !!equipmentId;

    const found = inspections.find((val) => {
        if (useEquipment) {
            return (
                val.InspectionId === inspectionId &&
                val.Equipment === equipmentId
            );
        }
        return (
            val.InspectionId === inspectionId && val.WorkOrder === workOrderId
        );
    });
    return found ?? null;
};

export const saveInspectionToLocal = async (
    woNumber: string, 
    equipmentId: string, 
    inspectionId: string, 
    pointValues: PointValue[], 
    inspectionDef: InspectionDef, 
    failedFlag: boolean, 
    comments?: string, 
    testEquipment?: string,
    pointImages?: { [key: string]: InspectionPointImage[] },
    pointComments?: { [key: string]: string }
) => {
    const enrichedPointValues = pointValues.map(point => {
        if (point.Images !== undefined && point.Comments !== undefined) {
            return point; 
        } else {
            const key = `${point.PointId}_${point.ObjectId}`;
            return {
                ...point,
                Images: pointImages?.[key] || [],
                Comments: pointComments?.[key] || ""
            };
        }
    });

    const isEquipmentContext = !woNumber && !!equipmentId;

    const data = await getInspectionResultsData(
        woNumber, 
        inspectionId, 
        enrichedPointValues, 
        inspectionDef, 
        failedFlag, 
        comments, 
        testEquipment,
        pointImages,
        pointComments,
        equipmentId,
        isEquipmentContext
    );
    
    const localInspections = await getLocalInspections();

    const newInspection: LocalInspectionsData = {
        ActionResult: {
            MessageList: [],
            Success: true,
        },
        Equipment: equipmentId,
        InspectionId: inspectionId,
        InspectionResultData: data,
        Messages: [],
        PrevStatus: "",
        Status: "InProcess",
        WorkOrder: woNumber,
        PointEqpList: {}
    };
    
    const updatedInspections = [
        ...localInspections.filter((insp: LocalInspectionsData) => {
            const useEquipment = !woNumber && !!equipmentId;
            if (useEquipment) {
                return !(
                    insp.InspectionId === inspectionId &&
                    insp.Equipment === equipmentId
                );
            }
            return !(
                insp.WorkOrder === woNumber &&
                insp.InspectionId === inspectionId
            );
        }),
        newInspection,
    ];

    return await setLocalInspections(updatedInspections).then(async () => {
        // Check that the inspection was saved to local
        const newlySaved = await getInspectionFromLocal(
            inspectionId,
            woNumber,
            equipmentId
        );
        return !!newlySaved;
    });
};

export const removeInspectionFromLocal = async (
    inspectionId: string,
    workOrderId: string,
    equipmentId?: string
): Promise<void> => {
    const allLocalInspections = await getLocalInspections();
    if (allLocalInspections) {
        const useEquipment = !workOrderId && !!equipmentId;
        const remainingInspections = allLocalInspections.filter(
            (insp: LocalInspectionsData) => {
                if (useEquipment) {
                    return !(
                        insp.InspectionId === inspectionId &&
                        insp.Equipment === equipmentId
                    );
                }
                return !(
                    insp.WorkOrder === workOrderId &&
                    insp.InspectionId === inspectionId
                );
            }
        );
        await setLocalInspections(remainingInspections);
    }
};